import type { ContainsGroup, Meal, MealIngredientItem } from './types';

export type { ContainsGroup };

export interface RawIngredient {
  name: string;
  grams: number | null;
  contains: ContainsGroup[];
}

export const CONTAINS_GROUP_LABELS: Record<ContainsGroup, string> = {
  gluten: 'Gluten',
  soy: 'Soy',
  'soy (trace)': 'Soy (trace)',
  dairy: 'Dairy',
};

// Common singular food stems. A trailing "s"/"es" is only stripped when the singular is listed here.
const KNOWN_STEMS = new Set([
  'egg', 'tomato', 'potato', 'onion', 'mushroom', 'pepper', 'bean', 'nut', 'oat', 'carrot', 'apple',
  'banana', 'cheese', 'olive', 'grape', 'lentil', 'pea', 'prawn', 'chip', 'cracker', 'noodle', 'sausage',
  'almond', 'walnut', 'cashew', 'strawberry', 'raspberry', 'blueberry', 'berry', 'orange',
  'lemon', 'lime', 'avocado', 'cucumber', 'courgette', 'chickpea', 'pancake', 'waffle', 'bagel',
  'crisp', 'biscuit', 'cookie', 'egg white', 'yolk', 'seed', 'date', 'fig', 'plum', 'pear', 'peach',
]);

function singular(word: string): string {
  if (word.endsWith('ies') && KNOWN_STEMS.has(`${word.slice(0, -3)}y`)) return `${word.slice(0, -3)}y`;
  if (word.endsWith('es') && KNOWN_STEMS.has(word.slice(0, -2))) return word.slice(0, -2);
  if (word.endsWith('s') && KNOWN_STEMS.has(word.slice(0, -1))) return word.slice(0, -1);
  return word;
}

export function normaliseIngredientName(name: string): string {
  const cleaned = (name || '').toLowerCase().replace(/[\s-]+/g, ' ').trim();
  if (!cleaned) return '';
  const words = cleaned.split(' ');
  words[words.length - 1] = singular(words[words.length - 1]);
  return words.join(' ');
}

// Gluten-free exclusion: explicit labels, or a gluten-free grain directly modifying a product word
// (adapted from INTOLERANCE_CATEGORIES in InsightsView.tsx, but no longer excluding any name that merely
// contains such a grain, so "almond croissant" or "corn and wheat tortilla" still count as gluten).
const GLUTEN_FREE_RE =
  /\b(gluten free|gf)\b|\btamari\b|\b(rice|corn|almond|coconut|tapioca|cassava|arrowroot|potato|buckwheat|sorghum|millet|teff|chickpea|maize) (flour|bread|pasta|noodles?|grain|crackers?)\b/i;
const GLUTEN_RE =
  /\b(wheat|gluten|bread|breadcrumbs?|pasta|sourdough|flour|barley|rye|spelt|couscous|semolina|noodles?|croissants?|bagels?|pancakes?|waffles?|pastry|cakes?|crackers?|toast)\b|\bsoy sauce\b|\bshoyu\b/i;

const SOY_TRACE_RE = /\b(soy|soya) lecithin\b|\be322\b|\b(soy|soya|soybean) oil\b/i;
const SOY_RE = /\b(soy|soya|soybeans?|tofu|tempeh|edamame|miso|tamari|shoyu|natto)\b/i;

const NOT_DAIRY_RE =
  /\b(oat|soy|soya|almond|coconut|rice|cashew|hazelnut|pea) milk\b|\blactose free\b|\bdairy free\b|\b(peanut|nut|almond|cashew|cocoa|shea|apple) butter\b|\bbutter beans?\b|\bbutternut\b|\bcoconut cream\b/i;
const DAIRY_RE =
  /\b(milk|cheese|cheddar|mozzarella|parmesan|butter|cream|yogh?urt|kefir|whey|casein|lactose|ghee|ricotta|custard|paneer|feta|brie|halloumi|mascarpone|dairy)\b/i;

export function groupsForIngredient(name: string): ContainsGroup[] {
  const n = normaliseIngredientName(name);
  if (!n) return [];
  const groups: ContainsGroup[] = [];
  if (GLUTEN_RE.test(n) && !GLUTEN_FREE_RE.test(n)) groups.push('gluten');
  if (SOY_TRACE_RE.test(n)) groups.push('soy (trace)');
  else if (SOY_RE.test(n)) groups.push('soy');
  if (DAIRY_RE.test(n) && !NOT_DAIRY_RE.test(n)) groups.push('dairy');
  return groups;
}

// Bare component words that describe a group rather than a food.
const COMPONENT_WORDS: Record<string, ContainsGroup> = {
  gluten: 'gluten',
  'wheat gluten': 'gluten',
  dairy: 'dairy',
  lactose: 'dairy',
  soy: 'soy',
};

function antiGroups(meal: Meal): Set<ContainsGroup> {
  const out = new Set<ContainsGroup>();
  for (const raw of (meal.anti_aggravates || '').split(',')) {
    const a = normaliseIngredientName(raw);
    if (a === 'gluten' || a === 'wheat') out.add('gluten');
    else if (a === 'dairy' || a === 'lactose') out.add('dairy');
    else if (a === 'soy' || a === 'soya') {
      out.add('soy');
      out.add('soy (trace)');
    }
  }
  return out;
}

interface RawEntry {
  name: string;
  grams: number | null;
  subGroups: ContainsGroup[];
}

function subIngredientGroups(items: MealIngredientItem[] | undefined): ContainsGroup[] {
  const out = new Set<ContainsGroup>();
  for (const sub of items || []) {
    groupsForIngredient(sub.canonicalName || sub.name).forEach((g) => out.add(g));
    subIngredientGroups(sub.subIngredients).forEach((g) => out.add(g));
  }
  return [...out];
}

function structuredEntries(items: MealIngredientItem[], multiplier: number): RawEntry[] {
  return items.map((item) => ({
    name: item.canonicalName || item.name,
    grams: typeof item.gramsEstimate === 'number' && Number.isFinite(item.gramsEstimate) ? item.gramsEstimate * multiplier : null,
    subGroups: subIngredientGroups(item.subIngredients),
  }));
}

function collectEntries(meal: Meal): RawEntry[] {
  const fromDishes = (meal.dishes || []).flatMap((d) =>
    structuredEntries(d.ingredients || [], d.portionMultiplier ?? meal.portionMultiplier ?? 1)
  );
  if (fromDishes.length > 0) return fromDishes;
  const fromMeal = structuredEntries(meal.ingredients || [], meal.portionMultiplier ?? 1);
  if (fromMeal.length > 0) return fromMeal;
  return (meal.ingredients_tags || '')
    .split(',')
    .map((t) => ({ name: t, grams: null, subGroups: [] }));
}

interface Parsed {
  ingredients: RawIngredient[];
  componentGroups: Set<ContainsGroup>;
}

function parseMeal(meal: Meal): Parsed {
  const anti = antiGroups(meal);
  const byName = new Map<string, RawIngredient>();
  const componentGroups = new Set<ContainsGroup>();
  for (const entry of collectEntries(meal)) {
    const name = normaliseIngredientName(entry.name);
    if (!name) continue;
    const component = COMPONENT_WORDS[name];
    if (component) {
      componentGroups.add(component);
      continue;
    }
    const groups = [...groupsForIngredient(name), ...entry.subGroups].filter((g) => !anti.has(g));
    const existing = byName.get(name);
    if (existing) {
      if (entry.grams !== null) existing.grams = (existing.grams ?? 0) + entry.grams;
      for (const g of groups) if (!existing.contains.includes(g)) existing.contains.push(g);
    } else {
      byName.set(name, { name, grams: entry.grams, contains: [...new Set(groups)] });
    }
  }
  for (const g of anti) componentGroups.delete(g);
  return { ingredients: [...byName.values()], componentGroups };
}

export function getMealIngredients(meal: Meal): RawIngredient[] {
  return parseMeal(meal).ingredients;
}

export function getMealGroups(meal: Meal): ContainsGroup[] {
  const { ingredients, componentGroups } = parseMeal(meal);
  const groups = new Set<ContainsGroup>(componentGroups);
  for (const i of ingredients) i.contains.forEach((g) => groups.add(g));
  return [...groups];
}
